// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { Rectangle } from '@shared/commons/Geometry';
import { Cursors } from '@shared/commons/Cursors';
import { TerceraChartCashItemSeriesCacheScreenDataDateChangeType } from '../../Series/TerceraChartCashItemSeriesEnums';
import { Line, PolyLine, PolyText, PolyTextItem, SolidBrush } from '@shared/commons/Graphics';
import { TerceraChartBaseScaleRenderer } from './TerceraChartBaseScaleRenderer';
import { ChartDataType } from '../../Utils/ChartConstants';
import { TerceraChartAction, TerceraChartActionEnum } from '../../TerceraChartAction';
import { ChartZoomRendererButtons } from '../TerceraChartZoomRenderer';
import { TerceraChartPriceScaleRendererMouseState } from './TerceraChartPriceScaleRenderer';
import { Periods } from '@shared/utils/History/TFInfo';
import { DateTimeUtils } from '@shared/utils/Time/DateTimeUtils';
import { ThemeManager } from '@front/controls/misc/ThemeManager';
import { MouseButtons } from '@front/controls/UtilsClasses/ControlsUtils';
import { LayersEnum } from '../../Utils/LayersEnum';
export class TerceraChartTimeScaleRenderer extends TerceraChartBaseScaleRenderer {
    constructor(chartActionProcessor, settings, terceraChart) {
        super(settings, terceraChart);
        this.mouseState = TerceraChartPriceScaleRendererMouseState.None;
        this.Series = null;
        this._separatorLinesInfo = [];
        this.YPADDING = 4;
        this.settings = settings;
        this.chartActionProcessor = chartActionProcessor;
        this.SetClassName('TerceraChartTimeScaleRenderer');
    }
    /// <summary>
    /// Алгоритм:
    /// Засечки на шкале представлены в виде нескольких уровней: обычная, смена дня, недели, месяца, года. В первую очередь должны быть нарисованы
    /// старшие уровни, плюс засечки не должны налазить друга на друга и не находится чаще, чем stepPixel на одну засечку.
    /// В основном проходе по серии анализируем засечки и сохраняем их отедльно оп типам (allItems). Потом начинаем рисование со старших уровней,
    /// запоминая занятые диапозон пикселей, если в дальнейшем, младшая засечка не поместиться (IsCanBePlaced), она не будет рисоваться.
    /// </summary>
    Draw(gr, window, windowsContainer, advParams) {
        this._separatorLinesInfo = [];
        const R = this.Rectangle;
        const RX = R.X;
        const RY = R.Y;
        const RW = R.Width;
        const RH = R.Height;
        const RB = RX + RW;
        gr.FillRect(this.settings.scaleBackBrush, RX, RY, RW, RH);
        const cashItemSeries = this.Series;
        if (cashItemSeries == null) {
            return;
        }
        const screenData = cashItemSeries.ChartScreenData;
        const allItems = [];
        for (let i = 0; i < 5; i++) {
            allItems.push([]);
        }
        const PixelMap = [];
        for (let i = 0; i < RB; i++) {
            PixelMap.push(false);
        }
        const TEXT_PADDING_ADDITIONAL = 4;
        const TEXT_PLATE_SIZE = 14;
        //
        // 1. Определяем подходящий шаг в пикселях (не чаще чем)
        // Пока фиксируем, потом возможно будем реагировать на шрифт
        //
        const stepPixel = 80;
        //
        // 2. Определяем подходящий шаг в минутах
        //
        let stepMin = -1;
        const currentPeriod = cashItemSeries.CashItem.FPeriod;
        const isCustomTimeframe = cashItemSeries.CashItem.TimeFrameInfo.ChartDataType !== ChartDataType.Default ||
            !Periods.IsStandardPeriod(currentPeriod);
        // Для кастом таймфреймов и тайм агрегаций отличных от Time выключаем логику
        if (cashItemSeries.CashItem.ChartDataType !== ChartDataType.Default ||
            !Periods.IsStandardPeriod(currentPeriod)) {
            stepMin = 0;
        }
        else {
            // Для остальных пытаемся подобрать подходящий шаг кратности
            const stepsMinutes = TerceraChartTimeScaleRenderer.stepsMinutes;
            for (let i = 0; i < stepsMinutes.length; i++) {
                if ((stepsMinutes[i] / currentPeriod) * window.XScale >= stepPixel) {
                    stepMin = stepsMinutes[i];
                    break;
                }
            }
            if (stepMin === -1) {
                stepMin = stepsMinutes[stepsMinutes.length - 1];
            }
            // По идее не должно быть такого, столкнулся на месяце при крупном масштабе
            if (stepMin < currentPeriod) {
                stepMin = currentPeriod;
            }
        }
        if (screenData.Storage.length < 1) {
            return;
        }
        // 3. Расчитываем данные для шкалы
        const scX = window.XScale;
        const lastScreenDate = screenData.Storage[screenData.Storage.length - 1].Time;
        let curX = Math.ceil(window.PointsConverter.GetScreenXbyTime(lastScreenDate));
        let lastDrawingX = RX;
        let lastDrawedData = null;
        const len = screenData.Storage.length;
        for (let i = len - 1; i >= 0; i--) {
            const curData = screenData.Storage[i];
            const dt = screenData.Storage[i].DateTime;
            const dtInSessionTimeZoneWithOffset = screenData.Storage[i].DateTimeInSessionTimeZoneWithOffset;
            const isDateSeparator = curData.DateChangeType !== TerceraChartCashItemSeriesCacheScreenDataDateChangeType.None;
            const currentDrawingX = Math.floor(curX + window.XScale / 2);
            let isMultiplier = false;
            if (Math.abs(currentDrawingX - lastDrawingX) < stepPixel) {
                if (isDateSeparator &&
                    lastDrawedData !== null &&
                    curData.DateChangeType > lastDrawedData.DateChangeType) {
                    const oldIndex = this.CheckDateChangeType(lastDrawedData.DateChangeType);
                    const oldList = allItems[oldIndex];
                    if (oldList.length > 0) {
                        const oldItem = oldList[oldList.length - 1];
                        if (oldItem.X === lastDrawingX) {
                            oldList.pop();
                        }
                    }
                    lastDrawingX = 0;
                    lastDrawedData = null;
                    isMultiplier = true;
                }
            }
            else if (isDateSeparator) {
                isMultiplier = true;
            }
            else if (stepMin < Periods.DAY && !isCustomTimeframe) {
                isMultiplier = (dt.getHours() * 60 + dt.getMinutes()) % stepMin === 0;
            }
            else if (stepMin === currentPeriod) {
                isMultiplier = true;
            }
            else {
                isMultiplier = Math.abs(currentDrawingX - lastDrawingX) >= stepPixel;
            }
            if (isMultiplier) {
                lastDrawedData = curData;
                lastDrawingX = currentDrawingX;
                let text = '';
                let curFont = this.settings.ScaleFont;
                let activeList = allItems[0];
                //
                // Видимость переходов может быть выключена, рисуем предыдущий переход в таком случае
                //
                let curDataDateChangeType = curData.DateChangeType; // TerceraChartCashItemSeriesCacheScreenDataDateChangeType
                curDataDateChangeType = this.CheckDateChangeType(curDataDateChangeType);
                const correctDateTime = currentPeriod >= Periods.DAY ? dtInSessionTimeZoneWithOffset : dt;
                switch (curDataDateChangeType) {
                    case TerceraChartCashItemSeriesCacheScreenDataDateChangeType.None:
                        if (currentPeriod < Periods.TIC ||
                            (currentPeriod > 0 && currentPeriod % Periods.SECOND === 0)) {
                            text = DateTimeUtils.formatDate(dt, '0:HH:mm:ss');
                        }
                        else if (currentPeriod === Periods.TIC || currentPeriod === Periods.RANGE) {
                            text = DateTimeUtils.formatDate(dt, 'HH:mm:ss:SSS');
                        }
                        else if (currentPeriod >= Periods.DAY) {
                            // #41873 - Все что дневка и выше - подписи даты
                            text = DateTimeUtils.formatDate(correctDateTime, 'DD.MM.YYYY');
                        }
                        else {
                            text = DateTimeUtils.formatDate(dt, 'HH:mm');
                        }
                        activeList = allItems[0];
                        break;
                    case TerceraChartCashItemSeriesCacheScreenDataDateChangeType.Day:
                        text = DateTimeUtils.formatDate(correctDateTime, 'DD.MM.YYYY');
                        curFont = this.settings.TimeScaleImportantDatesFont;
                        activeList = allItems[1];
                        break;
                    case TerceraChartCashItemSeriesCacheScreenDataDateChangeType.Week:
                        text = DateTimeUtils.formatDate(correctDateTime, 'DD.MM.YYYY');
                        curFont = this.settings.TimeScaleImportantDatesFont;
                        activeList = allItems[2];
                        break;
                    case TerceraChartCashItemSeriesCacheScreenDataDateChangeType.Month:
                        text = DateTimeUtils.formatDate(correctDateTime, 'MMMM');
                        curFont = this.settings.TimeScaleImportantDatesFont;
                        activeList = allItems[3];
                        break;
                    case TerceraChartCashItemSeriesCacheScreenDataDateChangeType.Year:
                        text = DateTimeUtils.formatDate(correctDateTime, 'YYYY');
                        curFont = this.settings.TimeScaleImportantDatesFont;
                        activeList = allItems[4];
                        break;
                }
                //
                const textWidth = Math.round(gr.GetTextWidth(text, curFont)) + 2 * TEXT_PADDING_ADDITIONAL;
                const leftBorder = currentDrawingX - textWidth / 2;
                const rightBorder = curDataDateChangeType === TerceraChartCashItemSeriesCacheScreenDataDateChangeType.None
                    ? currentDrawingX + textWidth / 2
                    : currentDrawingX + textWidth + TEXT_PLATE_SIZE;
                if (leftBorder > RX && rightBorder < RB) {
                    activeList.push(new TerceraChartTimeScaleRendererItem(curDataDateChangeType, leftBorder, rightBorder, text, currentDrawingX, textWidth));
                }
            }
            curX -= scX;
        }
        //
        // Определение уровня шкалы, для которого будем использовать жирный шрифт
        //
        let boldLevel = 100;
        let filledLevelsCount = 0;
        for (let k = allItems.length - 1; k >= 0; k--) {
            if (allItems[k].length > 0) {
                if (boldLevel === 100) {
                    boldLevel = k;
                }
                filledLevelsCount++;
            }
        }
        if (filledLevelsCount < 2) {
            boldLevel = 100;
        }
        //
        // Рисование, на основе расчитанных данных
        //
        const windows = windowsContainer.Windows;
        const windowsLen = windows.length;
        const gridPolyLines = new PolyLine();
        const gridPolyLinesLines = gridPolyLines.lines;
        const gridPolyTextScaleFont = new PolyText();
        const gridPolyTextImportantDatesFont = new PolyText();
        let separatorPlaced = false;
        for (let k = allItems.length - 1; k >= 0; k--) {
            const Items = allItems[k];
            const ItemsLen = Items.length;
            for (let i = 0; i < ItemsLen; i++) {
                const Item = Items[i];
                const ItemX = Item.X;
                if (TerceraChartTimeScaleRenderer.IsCanBePlaced(Item, PixelMap)) {
                    /// / Текст
                    let itemText;
                    if (Item.DataChangeType === TerceraChartCashItemSeriesCacheScreenDataDateChangeType.None) {
                        itemText = new PolyTextItem(Item.Text, ItemX, RY + this.YPADDING);
                    }
                    else {
                        itemText = new PolyTextItem(Item.Text, ItemX + Item.Width / 2 + 9, RY + this.YPADDING);
                    }
                    if (k === boldLevel) {
                        gridPolyTextImportantDatesFont.textArray.push(itemText);
                    }
                    else {
                        gridPolyTextScaleFont.textArray.push(itemText);
                    }
                    //
                    // Draw separators
                    //
                    let separatorPen = null;
                    let platePen = null;
                    let separatorColor = '';
                    let separatorText = null;
                    switch (Item.DataChangeType) {
                        case TerceraChartCashItemSeriesCacheScreenDataDateChangeType.Day:
                            if ((currentPeriod <= Periods.HOUR4 || currentPeriod % Periods.RANGE === 0) &&
                                this.settings.DaySeparatorsVisibility) {
                                separatorPen = this.settings.daySeparatorsPen;
                                platePen = this.settings.dayBorderSeparatorsPen;
                                separatorColor = ThemeManager.colors.Chart_DaySeparateColor;
                                separatorText = 'D';
                                separatorPlaced = true;
                            }
                            break;
                        case TerceraChartCashItemSeriesCacheScreenDataDateChangeType.Week:
                            if (this.settings.WeekSeparatorsVisibility) {
                                separatorPen = this.settings.weekSeparatorsPen;
                                platePen = this.settings.weekBorderSeparatorsPen;
                                separatorColor = ThemeManager.colors.Chart_WeekSeparateColor;
                                separatorText = 'W';
                                separatorPlaced = true;
                            }
                            break;
                        case TerceraChartCashItemSeriesCacheScreenDataDateChangeType.Month:
                            if (this.settings.MonthSeparatorsVisibility) {
                                separatorPen = this.settings.monthSeparatorsPen;
                                platePen = this.settings.monthBorderSeparatorsPen;
                                separatorColor = ThemeManager.colors.Chart_MonthSeparateColor;
                                separatorText = 'M';
                                separatorPlaced = true;
                            }
                            break;
                        case TerceraChartCashItemSeriesCacheScreenDataDateChangeType.Year:
                            if (this.settings.YearSeparatorsVisibility) {
                                separatorPen = this.settings.yearSeparatorsPen;
                                platePen = this.settings.yearBorderSeparatorsPen;
                                separatorColor = ThemeManager.colors.Chart_YearSeparateColor;
                                separatorText = 'Y';
                                separatorPlaced = true;
                            }
                            break;
                    }
                    if (separatorPen != null) {
                        const separatorPolyLines = new PolyLine();
                        const separatorPolyLinesLines = separatorPolyLines.lines;
                        for (let i = 0; i < windowsLen; i++) {
                            const windowClientRect = windows[i].ClientRectangle;
                            const windowClientRectY = windowClientRect.Y;
                            separatorPolyLinesLines.push(new Line(ItemX, windowClientRectY, ItemX, windowClientRectY + windowClientRect.Height));
                        }
                        this._separatorLinesInfo.push({ pen: separatorPen, polyLine: separatorPolyLines });
                        const font = this.settings.ScaleFont;
                        const rectWidth = 16;
                        const rect = new Rectangle(Math.round(ItemX - rectWidth / 2), RY, rectWidth, RH);
                        const plateBrush = new SolidBrush(separatorColor);
                        gr.RoundRectangle(rect, 4, plateBrush);
                        gr.DrawStringInRect(separatorText, font, this.settings.textSeparatorBrush, rect, false, 'center', 'middle');
                    }
                }
                // Сетка
                if (this.settings.ScaleGridVisibility && !separatorPlaced) {
                    for (let j = 0; j < windowsLen; j++) {
                        const windowClientRect = windows[j].ClientRectangle;
                        const windowClientRectY = windowClientRect.Y;
                        gridPolyLinesLines.push(new Line(ItemX, windowClientRectY, ItemX, windowClientRectY + windowClientRect.Height));
                    }
                }
                else {
                    separatorPlaced = false;
                }
            }
        }
        gr.DrawPolyLine(this.settings.scaleGridPen, gridPolyLines);
        gr.DrawPolyText(gridPolyTextImportantDatesFont, this.settings.TimeScaleImportantDatesFont, this.settings.scaleTextBrush, 'center', 'top');
        gr.DrawPolyText(gridPolyTextScaleFont, this.settings.ScaleFont, this.settings.scaleTextBrush, 'center', 'top');
    }
    DrawSeparatorLines(gr) {
        for (let i = 0; i < this._separatorLinesInfo.length; i++) {
            const info = this._separatorLinesInfo[i];
            gr.DrawPolyLine(info.pen, info.polyLine);
        }
    }
    CheckDateChangeType(changeType) {
        let res = changeType;
        switch (changeType) {
            case TerceraChartCashItemSeriesCacheScreenDataDateChangeType.Week:
                if (!this.settings.WeekSeparatorsVisibility) {
                    res = TerceraChartCashItemSeriesCacheScreenDataDateChangeType.Day;
                }
                break;
            case TerceraChartCashItemSeriesCacheScreenDataDateChangeType.Month:
                if (!this.settings.MonthSeparatorsVisibility) {
                    res = TerceraChartCashItemSeriesCacheScreenDataDateChangeType.Day;
                }
                break;
            case TerceraChartCashItemSeriesCacheScreenDataDateChangeType.Year:
                if (!this.settings.YearSeparatorsVisibility) {
                    res = TerceraChartCashItemSeriesCacheScreenDataDateChangeType.Month;
                    res = this.CheckDateChangeType(res);
                }
                break;
            default:
                break;
        }
        return res;
    }
    GetPreferredHeight() {
        let maxH = this.settings.ScaleFont.getCorrectHeight();
        const curH = this.settings.TimeScaleImportantDatesFont.getCorrectHeight();
        if (maxH < curH) {
            maxH = curH;
        }
        return maxH + this.YPADDING * 2;
    }
    static IsCanBePlaced(item, PixelMap) {
        if (item.LeftBorder >= PixelMap.length - 1) {
            return false;
        }
        // Check
        if (item.LeftBorder < 0) {
            item.LeftBorder = 0;
        }
        if (item.RightBorder < 0) {
            item.RightBorder = 0;
        }
        if (item.RightBorder > PixelMap.length - 1) {
            item.RightBorder = PixelMap.length - 1;
        }
        // Check free place - light
        if (PixelMap[item.LeftBorder] || PixelMap[item.RightBorder]) {
            return false;
        }
        // Check free place - full
        let len = item.RightBorder;
        for (let i = item.LeftBorder + 1; i < len; i++) {
            if (PixelMap[i]) {
                return false;
            }
        }
        // Store place
        len = item.RightBorder;
        for (let i = item.LeftBorder; i < len; i++) {
            PixelMap[i] = true;
        }
        return true;
    }
    // #region Process mouse events
    ProcessMouseDown(e) {
        if (!this.Visible || e.Button !== MouseButtons.Left) {
            return false;
        }
        this.mouseState = TerceraChartPriceScaleRendererMouseState.None;
        if (this.Rectangle.Contains(e.Location.X, e.Location.Y)) {
            this.mouseState = TerceraChartPriceScaleRendererMouseState.Moving;
            this.lastMouseDownPt = e.Location;
            return true;
        }
        return false;
    }
    /// <summary>
    ///
    /// </summary>
    ProcessMouseMove(e) {
        if (!this.Visible) {
            return false;
        }
        if (this.mouseState === TerceraChartPriceScaleRendererMouseState.Moving) {
            const newX = this.lastMouseDownPt.X;
            const xDeltaPx = e.Location.X - newX;
            if (Math.abs(xDeltaPx) > 15) {
                if (xDeltaPx > 0) {
                    this.chartActionProcessor.ProcessTerceraChartAction(TerceraChartAction.Create(TerceraChartActionEnum.Zoom, ChartZoomRendererButtons.ZoomIn));
                }
                else {
                    this.chartActionProcessor.ProcessTerceraChartAction(TerceraChartAction.Create(TerceraChartActionEnum.Zoom, ChartZoomRendererButtons.ZoomOut));
                }
                this.lastMouseDownPt = e.Location;
            }
            e.NeedRedraw = LayersEnum.All;
            return true;
        }
        return TerceraChartBaseScaleRenderer.prototype.ProcessMouseMove.call(this, e);
    }
    ProcessMouseUp(e) {
        this.mouseState = TerceraChartPriceScaleRendererMouseState.None;
        TerceraChartBaseScaleRenderer.prototype.ProcessMouseUp.call(this, e);
        return false;
    }
    // #endregion
    GetCursor(e) {
        if (this.mouseState === TerceraChartPriceScaleRendererMouseState.Moving &&
            this.Rectangle.Contains(e.Location.X, e.Location.Y)) {
            return Cursors.SizeWE;
        }
        return null;
    }
    /// <summary>
    /// Context menu for selected tools
    /// </summary>
    GetContextMenu(e, chart) {
        const location = e.Location;
        if (!this.Visible || !this.Rectangle.Contains(location.X, location.Y)) {
            return super.GetContextMenu(e, chart);
        }
        const timeSeparatorsMenuItem = chart.TerceraChartActionProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.TimeSeparators));
        timeSeparatorsMenuItem.subitems = [
            chart.TerceraChartActionProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.TimeSeparators, TerceraChartCashItemSeriesCacheScreenDataDateChangeType.Day)),
            chart.TerceraChartActionProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.TimeSeparators, TerceraChartCashItemSeriesCacheScreenDataDateChangeType.Month)),
            chart.TerceraChartActionProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.TimeSeparators, TerceraChartCashItemSeriesCacheScreenDataDateChangeType.Year))
        ];
        return [
            timeSeparatorsMenuItem,
            chart.TerceraChartActionProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.ShowHoles)),
            chart.TerceraChartActionProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.ShowExtendedSession))
        ];
    }
}
// TerceraChartTimeScaleRenderer.prototype = Object.create(TerceraChartBaseScaleRenderer.prototype);
/// <summary>
/// Набор фиксированных шагов для шкалы (в минутах)
/// </summary>
TerceraChartTimeScaleRenderer.stepsMinutes = [1, 5, 15, 30, 60, 240, 1440, 2880, 4320, 5760, 7200, 14400, 43200, 86400, 129600, 432000];
/// <summary>
/// Итем для хранения данных по шкале
/// </summary>
class TerceraChartTimeScaleRendererItem {
    constructor(DataChangeType, LeftBorder, RightBorder, Text, x, width) {
        this.DataChangeType = DataChangeType;
        this.LeftBorder = Math.floor(LeftBorder);
        this.RightBorder = Math.floor(RightBorder);
        this.Text = Text;
        this.X = x;
        this.Width = width;
    }
}
